import { Icon } from "icons";
import { observer } from "mobx-react-lite";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  PopoverTrigger,
  Tabs,
  TabsList,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "ui";

import { ExerciseAlternateTab } from "src/components/ExerciseAlternateTab";
import { ExerciseMainTab } from "src/components/ExerciseMainTab";
import { HorizontalFadeScrollArea } from "src/components/HorizontalFadeScrollArea";
import { ExerciseCombobox } from "src/workouts/ExerciseCombobox";
import { ExerciseSearchContextProvider } from "src/workouts/ExerciseSearchContext";
import {
  getDropIndicatorEdge,
  useAlternateDragAndDrop,
} from "src/workouts/useAlternateDragAndDrop";
import { type ExerciseAlternateActions } from "src/workouts/useExerciseAlternateActions";
import { useWorkoutEditStore } from "src/workouts/WorkoutEditContext";
import {
  type AlternateWorkoutItemData,
  type ExerciseData,
} from "src/workouts/WorkoutEditStore";

interface WorkoutItemAlternatesProps {
  workoutItemId: string;
  activeWorkoutItemId: string;
  mainExercise: ExerciseData;
  alternates: AlternateWorkoutItemData[];
  canEdit: boolean;
  actions: ExerciseAlternateActions;
}

// The main exercise and its alternates, one tab each.
export const WorkoutItemAlternates = observer(function WorkoutItemAlternates(
  props: WorkoutItemAlternatesProps,
) {
  const { t } = useTranslation();
  const workoutEditStore = useWorkoutEditStore();
  const dndInstanceId = useId();
  const [isComboboxOpen, setIsComboboxOpen] = useState(false);
  const actions = props.actions;

  const dropSlot = useAlternateDragAndDrop({
    dndInstanceId,
    getAlternateIds: actions.getAlternateIds,
    onReorder: actions.reorder,
  });

  function handleAddExercise(exercise: { id: string; name: string }) {
    const existingIds = props.alternates.map((alt) => {
      return alt.exercise?.id;
    });

    if (existingIds.includes(exercise.id)) {
      return;
    }

    if (exercise.id === props.mainExercise.id) {
      return;
    }

    actions.add(exercise.id);
  }

  function handleValueChange(value: string) {
    workoutEditStore.selectAlternate(
      props.workoutItemId,
      value === props.workoutItemId ? null : value,
    );
  }

  return (
    <ExerciseSearchContextProvider enabled>
      <Tabs
        value={props.activeWorkoutItemId}
        // The context menu returns focus to its tab when it closes; focus alone
        // must not select it, or it would undo mark as main's switch to Main.
        activationMode="manual"
        onValueChange={handleValueChange}
        className="min-w-0 items-start px-2 pt-2 data-[orientation=horizontal]:flex-row"
      >
        <HorizontalFadeScrollArea>
          <TabsList className="justify-start">
            <ExerciseMainTab
              value={props.workoutItemId}
              exercise={props.mainExercise}
            />
            {props.alternates.map((alternate, index) => {
              return (
                <ExerciseAlternateTab
                  key={alternate.id}
                  alternate={alternate}
                  index={index}
                  dndInstanceId={dndInstanceId}
                  canEdit={props.canEdit}
                  isPending={actions.isPending}
                  dropIndicatorEdge={getDropIndicatorEdge(
                    dropSlot,
                    index,
                    props.alternates.length,
                  )}
                  onMakeMain={actions.makeMain}
                  onRemove={actions.remove}
                />
              );
            })}
          </TabsList>
        </HorizontalFadeScrollArea>
        {/* Adding while a swap is pending would add to swapped content. */}
        {props.canEdit && !actions.isMakingMain ? (
          <ExerciseCombobox
            isOpen={isComboboxOpen}
            setIsOpen={setIsComboboxOpen}
            onChange={handleAddExercise}
            className="min-w-80"
            trigger={
              <Tooltip>
                <TooltipTrigger asChild>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon-sm"
                      className="shrink-0"
                      loading={actions.isAdding}
                      aria-label={t("workoutItem.addAlternate")}
                    >
                      <Icon name="tabler-plus" />
                    </Button>
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent>{t("workoutItem.addAlternate")}</TooltipContent>
              </Tooltip>
            }
          />
        ) : null}
      </Tabs>
    </ExerciseSearchContextProvider>
  );
});
